import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export type PortalActionMenuItem =
  | {
      key: string;
      type: "button";
      label: React.ReactNode;
      onClick: () => void;
      tone?: "default" | "destructive";
      isActive?: boolean;
    }
  | {
      key: string;
      type: "title";
      label: string;
    }
  | {
      key: string;
      type: "divider";
    };

type PortalActionMenuProps = {
  items: PortalActionMenuItem[];
  buttonLabel?: React.ReactNode;
  onBeforeOpen?: (e: React.MouseEvent) => void;
};

/**
 * テーブルの overflow コンテナ内でも正しく前面表示される Portal ベースのアクションメニュー。
 * ボタンクリックで document.body に固定配置のメニューを表示する。
 * trigger ボタンとメニューのスタイルは内部固定で、className 系 prop は持たない。
 * @param {object} props プロパティ
 * @param {PortalActionMenuItem[]} props.items メニュー項目の配列
 * @param {React.ReactNode} [props.buttonLabel] ボタンのラベル（デフォルト: "操作 ▾"）
 * @param {Function} [props.onBeforeOpen] メニュー表示前のコールバック（イベント伝播停止等）
 * @return {React.ReactElement} Portal ベースのアクションメニュー
 */
export const PortalActionMenu: React.FC<PortalActionMenuProps> = ({
  items,
  buttonLabel = "操作 ▾",
  onBeforeOpen,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({
    top: -9999,
    left: -9999,
  });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const handleToggle = useCallback(
    (e: React.MouseEvent) => {
      onBeforeOpen?.(e);
      setIsOpen(prev => !prev);
    },
    [onBeforeOpen],
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Calculate and adjust position after menu is rendered in the DOM
  useEffect(() => {
    if (!isOpen || !menuRef.current || !buttonRef.current) return;

    // Use requestAnimationFrame to ensure the menu is painted and has dimensions
    const rafId = requestAnimationFrame(() => {
      const button = buttonRef.current;
      const menu = menuRef.current;
      if (!button || !menu) return;

      const buttonRect = button.getBoundingClientRect();
      const menuRect = menu.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      // Default: below button, right-aligned (dropdown-end)
      let top = buttonRect.bottom + 4;
      let left = buttonRect.right - menuRect.width;

      // If menu overflows left, align to button's left edge
      if (left < 0) {
        left = buttonRect.left;
      }

      // If menu overflows bottom, show above the button
      if (top + menuRect.height > viewportHeight) {
        top = buttonRect.top - menuRect.height - 4;
      }

      // If menu overflows right
      if (left + menuRect.width > viewportWidth) {
        left = viewportWidth - menuRect.width - 8;
      }

      setPosition({ top, left });
    });

    return () => cancelAnimationFrame(rafId);
  }, [isOpen]);

  // Close on outside click, scroll, resize
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        menuRef.current &&
        !menuRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        handleClose();
      }
    };

    const handleScroll = () => {
      handleClose();
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleClose);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleClose);
    };
  }, [isOpen, handleClose]);

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-ghost btn-xs whitespace-nowrap"
        onClick={handleToggle}
      >
        {buttonLabel}
      </button>
      {isOpen &&
        createPortal(
          <ul
            ref={menuRef}
            className="menu p-2 shadow-lg bg-base-100 rounded-box w-44 fixed z-[9999]"
            style={{ top: position.top, left: position.left }}
          >
            {items.map(item => {
              if (item.type === "divider") {
                return (
                  <li
                    key={item.key}
                    className="mt-1 border-t border-base-300"
                  />
                );
              }
              if (item.type === "title") {
                return (
                  <li key={item.key} className="menu-title text-xs">
                    {item.label}
                  </li>
                );
              }
              return (
                <li key={item.key}>
                  <button
                    type="button"
                    className={[
                      item.isActive ? "active" : "",
                      item.tone === "destructive" ? "text-error" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      item.onClick();
                      handleClose();
                    }}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>,
          document.body,
        )}
    </>
  );
};
